// Token + base URL both come in as props — the exact pattern App.tsx's
// executeAgentCard uses for the same /plans/runCluster endpoint.
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { useToast } from "../../components/Toast";
import { trimTrailingSlash } from "../../lib/url";
import { reportPlanRequiredRunError } from "../../lib/api-client";
import { ToolkitItem } from "./catalog";
import { useSessionList } from "./useSessionList";
import { combineSummaries, fetchSessionSummaries, MissingSummariesError, sessionLabel } from "./multiSession";

const POLL_TIMEOUT = 120_000;
const POLL_INTERVAL = 2000;

type Props = {
  item: Extract<ToolkitItem, { kind: "prompt" }>;
  patientId: string; // globally selected client (may be "")
  apiBaseUrl: string;
  getToken: () => Promise<string>;
  onBack: () => void;
};

export const PromptWorkspace: React.FC<Props> = ({ item, patientId, apiBaseUrl, getToken, onBack }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const API_BASE = trimTrailingSlash(apiBaseUrl);

  // Client is inherited from the global header selector — no second picker here.
  // ToolkitPage keys this workspace on patientId, so a global switch remounts us.
  const client = patientId;
  const { sessions, loading: sessionsLoading, error: sessionsError } = useSessionList(apiBaseUrl, getToken, client);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Default to the newest session whenever the list changes. Adjusted
  // synchronously during render (React's documented "adjusting state when a
  // prop changes" pattern) rather than in an effect, so the very commit that
  // introduces the checkbox list already reflects the default selection —
  // an effect-based update would paint one frame of unchecked boxes first.
  const [sessionsForSelection, setSessionsForSelection] = useState(sessions);
  if (sessions !== sessionsForSelection) {
    setSessionsForSelection(sessions);
    setSelectedIds(sessions.length ? [sessions[0].session_id] : []);
  }
  // Stable serialization for staleness guards (order-independent).
  const selKey = [...selectedIds].sort().join(",");
  const selKeyRef = useRef(selKey);
  useEffect(() => { selKeyRef.current = selKey; }, [selKey]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);
  const clientRef = useRef(client);
  useEffect(() => { clientRef.current = client; }, [client]);

  // A different client/selection invalidates any previous output.
  useEffect(() => {
    setResult("");
    setError(null);
  }, [client, selKey]);

  const toggleSession = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const run = async () => {
    const ranClient = client;
    const ranSelKey = selKey;
    setRunning(true);
    setError(null);
    setResult("");
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");
      // Selected ids in list order (newest first) — list order drives both
      // the combined text (newest-last after reverse) and the POST target.
      const orderedSelected = sessions.filter((s) => selectedIds.includes(s.session_id));
      const newestId = orderedSelected[0].session_id;

      const payload: Record<string, unknown> = {
        cluster_id: `toolkit-${item.templateId}`,
        title: item.title,
        techniques: [item.title],
        pinned: [],
        template_ids: [item.templateId],
      };
      if (orderedSelected.length > 1) {
        // Multi-session: pre-fetch summaries and send the combined text.
        // Loud pre-run failure if any selected session is unsummarized.
        const entries = await fetchSessionSummaries(apiBaseUrl, getToken, orderedSelected.map((s) => s.session_id));
        payload.summary = combineSummaries(entries);
      }
      // Single session: no `summary` — run_cluster fetches it by session id
      // and hard-errors if the session has none. That error is surfaced below.

      const postResp = await fetch(`${API_BASE}/plans/runCluster?session=${encodeURIComponent(newestId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!postResp.ok) throw new Error(`API ${postResp.status}: ${await postResp.text().catch(() => "")}`);
      const { run_id } = await postResp.json();
      if (!run_id) throw new Error("No run_id returned");

      const start = Date.now();
      while (Date.now() - start < POLL_TIMEOUT) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        if (cancelledRef.current) return;
        const pollResp = await fetch(`${API_BASE}/plans/runCluster?run_id=${encodeURIComponent(run_id)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!pollResp.ok) throw new Error(`Poll error: ${pollResp.status}`);
        const data = await pollResp.json();
        if (cancelledRef.current) return;
        if (data.status === "done") {
          // run_cluster always emits `result`; its absence is a bug — be loud.
          if (!data.result) throw new Error("Worker returned no result");
          if (clientRef.current !== ranClient || selKeyRef.current !== ranSelKey) {
            // Pickers moved mid-run — this output no longer matches the UI context.
            setRunning(false);
            return;
          }
          setResult(data.result);
          setRunning(false);
          return;
        }
        if (data.status === "error") {
          // Entitlement failures arrive as 200 poll bodies, not HTTP 402.
          reportPlanRequiredRunError(data.error);
          throw new Error(data.error || "Worker error");
        }
      }
      throw new Error(t("toolkit.runTimeout"));
    } catch (e: any) {
      if (cancelledRef.current) return;
      const msg = e instanceof MissingSummariesError
        ? t("toolkit.noSummaryFor", { dates: e.labels.join(", ") })
        : String(e?.message || e);
      setError(msg);
      toast.error(msg);
      setRunning(false);
    }
  };

  const canRun = Boolean(client) && selectedIds.length > 0 && !running && !sessionsLoading;

  return (
    <div className="tk-workspace">
      <button type="button" className="tk-back" onClick={onBack}>← {t("toolkit.back")}</button>
      <div className="tk-ws-head">
        <h2 className="tk-ws-title">{item.title}</h2>
        <span className="tk-badge tk-badge--modality tk-badge--m-transcript">{t("toolkit.modality.transcript")}</span>
      </div>
      {item.description && <p className="tk-ws-desc">{item.description}</p>}
      {client && (
        <div className="tk-ws-controls">
          <fieldset className="tk-session-list" disabled={sessionsLoading}>
            <legend className="tk-session-list__legend">
              {t("toolkit.sessionsLabel")}
              {selectedIds.length > 0 && <span className="tk-session-list__count">{selectedIds.length}</span>}
            </legend>
            {sessions.map((s) => (
              <label key={s.session_id} className="tk-session-list__row">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(s.session_id)}
                  onChange={() => toggleSession(s.session_id)}
                />
                <span>{sessionLabel(s)}</span>
              </label>
            ))}
          </fieldset>
          <button type="button" className="tk-run" onClick={run} disabled={!canRun}>
            {running
              ? t("toolkit.running")
              : selectedIds.length > 1
              ? t("toolkit.runCount", { count: selectedIds.length })
              : t("toolkit.run")}
          </button>
        </div>
      )}
      {!client && <p className="tk-ws-desc">{t("toolkit.selectClientFirst")}</p>}
      {client && !sessionsLoading && !sessionsError && sessions.length === 0 && (
        <p className="tk-ws-desc">{t("toolkit.noSessions")}</p>
      )}
      {sessionsError && <div className="tk-error">{sessionsError}</div>}
      {error && <div className="tk-error">{error}</div>}
      {result && (
        <div className="tk-result">
          <button
            type="button"
            className="tk-chip"
            onClick={() => {
              navigator.clipboard.writeText(result).then(
                () => toast.success(t("toolkit.copied")),
                (e) => toast.error(String(e?.message || e))
              );
            }}
          >
            {t("toolkit.copy")}
          </button>
          <ReactMarkdown>{result}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};
